import React from 'react';
import {
  Typography,
  Box,
  Paper,
  Grid,
  Divider,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Button,
  CircularProgress,
  Alert,
  Stack,
  Card,
  CardContent,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VideocamIcon from '@mui/icons-material/Videocam';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

// Interfaces
interface Event {
  id?: string;
  eventName: string;
  eventDate: string;
  eventDescription?: string;
  location?: string;
  startTime?: string;
  endTime?: string;
  isVirtual?: boolean;
  meetingLink?: string;
}

interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  tags: string[];
}

interface EmailTemplate {
  subject: string;
  body: string;
  includeCalendarInvite: boolean;
  includeTripDetails: boolean;
}

interface ReviewStepProps {
  selectedEvent: Event | null;
  selectedContacts: Contact[];
  emailTemplate: EmailTemplate;
  sendingStatus: 'idle' | 'sending' | 'success' | 'error';
  onSend: () => void;
}

const ReviewStep: React.FC<ReviewStepProps> = ({
  selectedEvent,
  selectedContacts,
  emailTemplate,
  sendingStatus,
  onSend,
}) => {
  if (!selectedEvent) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <Typography variant="h6" color="error">
          No event selected. Please go back and select an event.
        </Typography>
      </Box>
    );
  }

  // Format date for display
  const formatDate = (dateInput: any): string => {
    if (!dateInput) return 'Date not set';
    
    try {
      let date;
      
      // Handle Firestore timestamp objects
      if (typeof dateInput === 'object' && dateInput !== null) {
        // Check if it's a Firestore timestamp (has seconds and nanoseconds)
        if (dateInput.seconds !== undefined && dateInput.nanoseconds !== undefined) {
          // Convert Firestore timestamp to JS Date
          date = new Date(dateInput.seconds * 1000);
        } else if (dateInput instanceof Date) {
          date = dateInput;
        } else {
          // If it's some other object, try to stringify it
          return String(dateInput);
        }
      } else {
        // Handle string dates
        date = new Date(dateInput);
      }
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return String(dateInput);
      }
      
      return date.toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      // Return a string representation as fallback
      return String(dateInput);
    }
  };

  // Render status component based on current status
  const renderStatusMessage = () => {
    switch (sendingStatus) {
      case 'sending':
        return (
          <Alert
            severity="info"
            icon={<CircularProgress size={20} />}
            sx={{ mt: 2 }}
          >
            Sending invitations to {selectedContacts.length} contacts...
          </Alert>
        );
      case 'success':
        return (
          <Alert
            severity="success"
            icon={<CheckCircleIcon />}
            sx={{ mt: 2 }}
          >
            Successfully sent invitations to {selectedContacts.length} contacts!
          </Alert>
        );
      case 'error':
        return (
          <Alert
            severity="error"
            icon={<ErrorIcon />}
            sx={{ mt: 2 }}
          >
            There was an error sending invitations. Please try again.
          </Alert>
        );
      default:
        return null;
    }
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review & Send Invitations
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Please review the event details, recipients, and email content before sending the invitations.
      </Typography>

      {renderStatusMessage()}

      <Grid container spacing={3} sx={{ mt: 1 }}>
        {/* Event Details */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <EventIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                Event Details
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Typography variant="h6" gutterBottom>
                {selectedEvent.eventName}
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <EventIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '0.9rem' }} />
                <Typography variant="body2">
                  {formatDate(selectedEvent.eventDate)}
                </Typography>
              </Box>
              
              {(selectedEvent.startTime || selectedEvent.endTime) && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AccessTimeIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '0.9rem' }} />
                  <Typography variant="body2">
                    {selectedEvent.startTime || 'TBD'} - {selectedEvent.endTime || 'TBD'}
                  </Typography>
                </Box>
              )}
              
              {selectedEvent.location && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {selectedEvent.isVirtual ? (
                    <VideocamIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '0.9rem' }} />
                  ) : (
                    <LocationOnIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '0.9rem' }} />
                  )}
                  <Typography variant="body2">
                    {selectedEvent.location}
                  </Typography>
                </Box>
              )}
              
              {selectedEvent.isVirtual && selectedEvent.meetingLink && (
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                  <VideocamIcon sx={{ color: 'text.secondary', mr: 1, fontSize: '0.9rem', mt: 0.3 }} />
                  <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                    {selectedEvent.meetingLink}
                  </Typography>
                </Box>
              )}
              
              {selectedEvent.eventDescription && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Description:
                  </Typography>
                  <Typography variant="body2" paragraph>
                    {selectedEvent.eventDescription}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Email Preview */}
        <Grid item xs={12} md={6}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                Email Preview
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Subject:
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {emailTemplate.subject}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Body:
                </Typography>
                <Box 
                  sx={{ 
                    whiteSpace: 'pre-wrap',
                    p: 2,
                    backgroundColor: '#f9f9f9',
                    borderRadius: 1,
                    mt: 1,
                    maxHeight: 200,
                    overflow: 'auto'
                  }}
                >
                  {emailTemplate.body}
                </Box>
              </Box>
              
              <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
                {emailTemplate.includeCalendarInvite && (
                  <Chip 
                    label="Calendar Invite (.ics)" 
                    size="small" 
                    color="primary"
                    variant="outlined"
                  />
                )}
                {emailTemplate.includeTripDetails && (
                  <Chip 
                    label="Trip Details" 
                    size="small" 
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Recipients */}
        <Grid item xs={12}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonIcon sx={{ mr: 1, fontSize: '1.2rem' }} />
                Recipients ({selectedContacts.length})
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {selectedContacts.length > 0 ? (
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  <List dense>
                    {selectedContacts.slice(0, 5).map((contact) => (
                      <ListItem key={contact.id}>
                        <ListItemAvatar>
                          <Avatar>
                            {contact.firstName[0]}{contact.lastName ? contact.lastName[0] : ''}
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={`${contact.firstName} ${contact.lastName}`}
                          secondary={contact.email}
                        />
                      </ListItem>
                    ))}
                  </List>
                  
                  {selectedContacts.length > 5 && (
                    <Box sx={{ textAlign: 'center', py: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        +{selectedContacts.length - 5} more contacts
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Typography variant="body2" color="error" sx={{ py: 2, textAlign: 'center' }}>
                  No contacts selected. Please go back and select contacts to invite.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary" paragraph>
          Ready to send invitations to {selectedContacts.length} contacts?
        </Typography>
        
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={onSend}
          disabled={sendingStatus === 'sending' || sendingStatus === 'success' || selectedContacts.length === 0}
          startIcon={sendingStatus === 'success' ? <MarkEmailReadIcon /> : <SendIcon />}
        >
          {sendingStatus === 'success' ? 'Invitations Sent!' : 'Send Invitations'}
        </Button>
      </Box>
    </Box>
  );
};

export default ReviewStep;
